import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, ChevronRight, ArrowRight, Check, 
  TrendingUp, AlertCircle, Sparkles, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HealthScore() {
  const { token, user, refreshUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/health-score/questions`);
      setQuestions(res.data.questions);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({ ...answers, [questionId]: answerIndex });
    
    // Auto advance to next question
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    }
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([qId, ans]) => ({
        question_id: qId,
        answer: ans
      }));
      
      const res = await axios.post(`${API}/health-score/submit`, 
        { answers: formattedAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setResult(res.data);
      refreshUser();
      toast.success(res.data.bonus);
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAssessment = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setResult(null);
    setShowIntro(true);
  };

  const progress = (Object.keys(answers).length / questions.length) * 100;
  const allAnswered = Object.keys(answers).length === questions.length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin w-8 h-8 border-2 border-[#0A1F44] border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  // Show Results
  if (result) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="neo-card p-8 text-center"
          >
            <p className="text-xs uppercase tracking-[0.3em] mb-6" style={{ color: '#C0975C' }}>Your Financial Health Score</p>
            {/* Score Circle */}
            <div className="relative w-48 h-48 mx-auto mb-6">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="#1a1a1a"
                  strokeWidth="12"
                  fill="none"
                />
                <motion.circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke={result.color}
                  strokeWidth="12"
                  fill="none"
                  strokeLinecap="round"
                  initial={{ strokeDasharray: "0 553" }}
                  animate={{ strokeDasharray: `${(result.score / 100) * 553} 553` }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span 
                  className="text-5xl font-black"
                  style={{ fontFamily: 'Outfit, sans-serif', color: result.color }}
                >
                  {result.score}
                </span>
                <span className="text-[#A3A3A3]">out of 100</span>
              </div>
            </div>

            {/* Grade */}
            <div 
              className="inline-block px-6 py-2 mb-4 font-black text-2xl"
              style={{ backgroundColor: result.color, color: '#000' }}
            >
              Grade: {result.grade}
            </div>
            
            <h2 className="text-2xl font-bold mb-2">{result.status}</h2>
            <p className="text-[#A3A3A3] mb-8">
              {result.score >= 60 
                ? "You're on the right track! Keep building good financial habits."
                : "Don't worry - we've got courses to help you improve!"}
            </p>

            {/* Recommendations */}
            <div className="text-left mb-8">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#C0975C]" />
                Recommendations for You
              </h3>
              <ul className="space-y-3">
                {result.recommendations.map((rec, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="flex items-start gap-3 p-3 bg-white/5"
                  >
                    <Check className="w-5 h-5 text-[#C0975C] mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                onClick={resetAssessment}
                className="flex-1 py-3 bg-white/10 hover:bg-white/20 font-bold flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Retake
              </button>
              <a
                href="/courses"
                className="flex-1 neo-button-primary py-3 font-bold flex items-center justify-center gap-2"
              >
                Start Learning
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Show Intro
  if (showIntro) {
    return (
      <Layout>
        <div className="p-6 lg:p-8 max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="neo-card p-8 text-center"
          >
            <div className="w-20 h-20 bg-[#0A1F44] mx-auto mb-6 flex items-center justify-center rounded-2xl">
              <Heart className="w-10 h-10 text-[#C0975C]" />
            </div>
            
            <h1 className="text-3xl font-black mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Financial <span className="text-[#C0975C]">Health Check</span>
            </h1>
            
            <p className="text-[#A3A3A3] mb-6">
              A quick 8-question snapshot of where your finances stand <em>right now</em>, with personalised tips to improve. (Different from your Financial Fitness Score, which grows as you keep learning.)
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8 text-center">
              <div className="p-4 bg-white/5">
                <p className="text-3xl font-bold text-[#C0975C]">8</p>
                <p className="text-sm text-[#A3A3A3]">Questions</p>
              </div>
              <div className="p-4 bg-white/5">
                <p className="text-3xl font-bold text-[#C0975C]">2</p>
                <p className="text-sm text-[#A3A3A3]">Minutes</p>
              </div>
              <div className="p-4 bg-white/5">
                <p className="text-3xl font-bold text-[#C0975C]">Instant</p>
                <p className="text-sm text-[#A3A3A3]">Result</p>
              </div>
            </div>

            <button
              onClick={() => setShowIntro(false)}
              className="neo-button-primary px-8 py-4 font-bold text-lg flex items-center gap-2 mx-auto"
            >
              Start Assessment
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Show Questions
  const currentQ = questions[currentQuestion];

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-2xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#A3A3A3]">Question {currentQuestion + 1} of {questions.length}</span>
            <span className="font-bold">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-white/10 h-2">
            <motion.div 
              className="bg-[#0A1F44] h-2"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="neo-card p-8"
          >
            <h2 className="text-xl font-bold mb-6">{currentQ.question}</h2>
            
            <div className="space-y-3">
              {currentQ.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleAnswer(currentQ.id, i)}
                  className={`w-full p-4 text-left transition-all flex items-center gap-3 ${
                    answers[currentQ.id] === i 
                      ? 'bg-[#0A1F44] text-black' 
                      : 'bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <span className={`w-8 h-8 flex items-center justify-center text-sm font-bold ${
                    answers[currentQ.id] === i ? 'bg-black text-[#0A1F44]' : 'bg-white/10'
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="font-medium">{option}</span>
                  {answers[currentQ.id] === i && (
                    <Check className="w-5 h-5 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex gap-4 mt-6">
          <button
            onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
            disabled={currentQuestion === 0}
            className="px-6 py-3 bg-white/10 disabled:opacity-30 font-bold"
          >
            Back
          </button>
          
          {currentQuestion < questions.length - 1 ? (
            <button
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              disabled={!answers[currentQ.id]}
              className="flex-1 neo-button-primary py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-30"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={submitAssessment}
              disabled={!allAnswered || submitting}
              className="flex-1 neo-button-primary py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-30"
            >
              {submitting ? 'Calculating...' : 'Get My Score'}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Question Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {questions.map((q, i) => (
            <button
              key={i}
              onClick={() => setCurrentQuestion(i)}
              className={`w-3 h-3 transition-all ${
                i === currentQuestion 
                  ? 'bg-[#0A1F44] scale-125' 
                  : answers[q.id] !== undefined 
                    ? 'bg-[#C0975C]' 
                    : 'bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </Layout>
  );
}
